/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { ComboBox, ComboBoxItem, FlexBox, Text } from '@ui5/webcomponents-react';
import React from 'react';
import { uiFullWidth } from '@/Styling/Theme/Theme';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '@/store/Products';
import { UserSettings } from '@/types';
import { browserLanguage, languagesSupported } from '@/commons';
import { getTextKeyByLanguageCode, getLanguageCodeByTextKey } from '@/utils/langUtils';
import { useTranslations } from '@/hooks/useTranslations';
import ComboBoxFilter from '@ui5/webcomponents/dist/types/ComboBoxFilter';

export const LanguageSwitch: React.FC = () => {
    const userSettings: UserSettings = useSelector((state: any) => state.productList.userSettings);
    const { locale } = userSettings;
    const dispatch = useDispatch<any>();
    const { t } = useTranslations();

    const handleOnInput = (event: Event): void => {
        const newLangCode = (event.target as HTMLInputElement)?.value;

        if (!newLangCode) {
            dispatch(
                actions.setUserSettings({
                    ...userSettings,
                    isValid: false,
                })
            );
            return;
        }

        const translatedLang = languagesSupported.map(lang => t(lang.textKey));
        const isValid = translatedLang.some(lang => lang.includes(newLangCode));
        const locale = getLanguageCodeByTextKey(newLangCode.toUpperCase());

        if (isValid) return;

        const updatedUserSettings = {
            ...userSettings,
            isValid,
            ...(locale && { locale }),
        };

        dispatch(actions.setUserSettings(updatedUserSettings));
    };

    const onChange = (event: any): void => {
        const newLangCode = event.detail.item.getAttribute('data-code') || browserLanguage;

        dispatch(
            actions.setUserSettings({
                ...userSettings,
                locale: newLangCode,
                isValid: true,
            })
        );
    };

    return (
        <FlexBox style={{ width: uiFullWidth.width }}>
            <ComboBox
                onInput={handleOnInput}
                onSelectionChange={onChange}
                style={{ width: '100%' }}
                value={t(getTextKeyByLanguageCode(locale))}
                data-testid="language-switch-wrapper"
                filter={ComboBoxFilter.StartsWith}
                valueState={userSettings.isValid ? 'None' : 'Negative'}
                valueStateMessage={<Text>{t('INVALID_ENTRY')}</Text>}
            >
                {languagesSupported.map(({ code, textKey }) => (
                    <ComboBoxItem key={code} text={t(textKey)} data-code={code} />
                ))}
            </ComboBox>
        </FlexBox>
    );
};

export default LanguageSwitch;
